import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Icon } from './Icon';

// What Salifly's auto-learning has stored about this rep — fully reviewable
// and deletable here. Rows come from the extract-knowledge edge function,
// which fires when a paid-plan rep sends a reply.
interface KnowledgeRow {
  id: string;
  content: string;
  type: 'business_fact' | 'tone_example';
  created_at: string;
}

function relativeDate(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86_400_000);
  if (days < 1) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return months < 12 ? `${months}mo ago` : `${Math.floor(months / 12)}y ago`;
}

interface KnowledgePageProps {
  userId: string;
}

export const KnowledgePage: React.FC<KnowledgePageProps> = ({ userId }) => {
  const [rows, setRows] = useState<KnowledgeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    supabase
      .from('user_knowledge_base')
      .select('id, content, type, created_at') // never pull the embedding column
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .then(({ data, error }) => {
        if (error) setDeleteError("Couldn't load your knowledge base — refresh to retry.");
        setRows((data as KnowledgeRow[]) ?? []);
        setLoading(false);
      });
  }, [userId]);

  async function handleDelete(row: KnowledgeRow) {
    setDeleteError(null);
    // Optimistic removal — restore on failure so the list never lies.
    setRows(prev => prev.filter(r => r.id !== row.id));
    const { error } = await supabase.from('user_knowledge_base').delete().eq('id', row.id);
    if (error) {
      setRows(prev => [row, ...prev].sort((a, b) => b.created_at.localeCompare(a.created_at)));
      setDeleteError("Couldn't delete that entry — try again.");
    }
  }

  const facts = rows.filter(r => r.type === 'business_fact');
  const tones = rows.filter(r => r.type === 'tone_example');

  if (loading) {
    return (
      <main className="main-content">
        <div className="main-container">
          <div className="insights-loading">
            <div className="insights-spinner" />
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="main-content">
      <div className="main-container">
        <div className="insights-hero">
          <h2 className="insights-title">Knowledge</h2>
          <p className="insights-sub">
            Everything Salifly has learned from your sent replies — used to ground your drafts, and fully under your control.
          </p>
        </div>

        {deleteError && <div className="kb-error-inline">{deleteError}</div>}

        {rows.length === 0 ? (
          <div className="insights-empty">
            <div className="insights-empty-icon">◎</div>
            <p>Nothing learned yet.</p>
            <p className="insights-empty-hint">
              On paid plans, Salifly automatically captures durable business facts (pricing, hours, plan details)
              and examples of your writing voice each time you send a reply. They appear here for review.
            </p>
          </div>
        ) : (
          <>
            <KnowledgeSection
              title="Business facts"
              hint="Facts Salifly treats as ground truth when drafting — pricing, hours, plan details."
              rows={facts}
              emptyText="No business facts captured yet."
              onDelete={handleDelete}
            />
            <KnowledgeSection
              title="Your voice"
              hint="Real replies you've sent. Salifly mirrors their cadence and vocabulary in new drafts."
              rows={tones}
              emptyText="No voice examples captured yet."
              onDelete={handleDelete}
            />
            <p className="kb-privacy-note">
              Voice examples contain messages you wrote. They're stored until you delete them here or delete your
              account, and are never used to train any AI model — see our Privacy Policy.
            </p>
          </>
        )}
      </div>
    </main>
  );
};

function KnowledgeSection({ title, hint, rows, emptyText, onDelete }: {
  title: string;
  hint: string;
  rows: KnowledgeRow[];
  emptyText: string;
  onDelete: (row: KnowledgeRow) => void;
}) {
  return (
    <div className="insights-section">
      <div className="insights-section-header">
        <h3 className="insights-section-heading">{title}</h3>
        <span className="kb-count">{rows.length}</span>
      </div>
      <p className="kb-section-hint">{hint}</p>
      {rows.length === 0 ? (
        <p className="kb-empty-section">{emptyText}</p>
      ) : (
        <div className="kb-list">
          {rows.map(row => (
            <div key={row.id} className="kb-row">
              <p className="kb-content">{row.content}</p>
              <div className="kb-row-side">
                <span className="kb-meta">{relativeDate(row.created_at)}</span>
                <button
                  className="kb-delete-btn"
                  title="Delete this entry"
                  onClick={() => onDelete(row)}
                >
                  <Icon name="trash" size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
